import { useCallback, useRef, useState } from 'react';

import {
  AddLaneSearchLanes,
  CreateLaneSearch,
  GetLaneSearch,
  LaneSearch,
  LaneSearchRequest,
  LaneSites,
  laneErrorMessage,
  RetryVendorSearch,
  unwrapLanePayload,
} from '../../../apis/lanePlanner';
import { usePollUntil } from './usePollUntil';

// the timing rules live with the shared poller; re-exported for callers and tests
export {
  nextPollDelayMs,
  POLL_INTERVAL_MS,
  POLL_RAMP_MS,
  retryDelayMs,
} from './usePollUntil';

/** Give up on a search that is still not done after this long. */
export const POLL_TIMEOUT_MS = 150000;

export interface LaneSearchState {
  /** The latest snapshot of the running or finished search. */
  search: LaneSearch | null;
  /** True from pressing Search until every vendor has answered (or polling gave up). */
  searching: boolean;
  /** A problem with the search as a whole; one vendor failing is in `search.vendors`. */
  error: string | null;
  start: (request: LaneSearchRequest) => Promise<void>;
  /** Opens an existing search (e.g. one the chat ran): shows it and follows it until done. Null if it could not be read. */
  load: (searchId: string) => Promise<LaneSearch | null>;
  /** Restarts each given vendor search (one after the other), then polls the search again. */
  retryVendors: (vendorSearchIds: string[]) => Promise<void>;
  /** Searches more airports or ports on the running search, then polls it again. */
  /** True when the lanes were accepted (or the search moved on); false when they were not. */
  addLanes: (sites: {
    origin_sites: LaneSites;
    destination_sites: LaneSites;
  }) => Promise<boolean>;
  reset: () => void;
}

/**
 * Runs one search at a time: creates it, then polls (`usePollUntil`) until
 * every vendor has answered. Each new start, reset, or unmount drops the
 * previous search's pending work, so a slow answer can never overwrite a newer
 * search.
 */
export const useLaneSearch = (token: string): LaneSearchState => {
  const [search, setSearch] = useState<LaneSearch | null>(null);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const poller = usePollUntil();
  /** Guards the create and retry calls, which are not polls. */
  const generation = useRef(0);
  /** The newest snapshot, for the deadline to read. */
  const latestSearch = useRef<LaneSearch | null>(null);

  const publish = useCallback((next: LaneSearch | null): void => {
    latestSearch.current = next;
    setSearch(next);
  }, []);

  const stop = useCallback((): void => {
    generation.current += 1;
    poller.stop();
  }, [poller]);

  /** Polls the search until it is done; the server says how soon to ask again. */
  const pollSearch = useCallback(
    (searchId: string): void => {
      poller.start({
        timeoutMs: POLL_TIMEOUT_MS,
        fetch: (signal) => GetLaneSearch(token, searchId, signal),
        onResult: (res) => {
          const latest = unwrapLanePayload<LaneSearch>(res);
          publish(latest);
          if (latest.done) {
            setSearching(false);
            return { done: true };
          }
          return { done: false, delayMs: latest.poll_after_ms };
        },
        onFail: (err) => {
          setSearching(false);
          setError(
            laneErrorMessage(err, 'Could not fetch rates. Please try again.')
          );
        },
        onTimeout: () => {
          setSearching(false);
          setError(
            latestSearch.current?.quotes.length
              ? 'Some vendors have not finished responding. Results may be incomplete.'
              : 'The search timed out. Please try again.'
          );
        },
      });
    },
    [poller, token, publish]
  );

  /** Shows a search from `fetchSearch` (a new one or an existing one) and
   * follows it until done. Null when it could not be read or was replaced. */
  const open = useCallback(
    async (
      fetchSearch: () => ReturnType<typeof GetLaneSearch>,
      failMessage: string
    ): Promise<LaneSearch | null> => {
      stop();
      const thisGeneration = generation.current;
      publish(null);
      setError(null);
      setSearching(true);
      try {
        const res = await fetchSearch();
        if (thisGeneration !== generation.current) return null;
        const opened = unwrapLanePayload<LaneSearch>(res);
        publish(opened);
        if (opened.done) setSearching(false);
        else pollSearch(opened.id);
        return opened;
      } catch (err) {
        if (thisGeneration !== generation.current) return null;
        setSearching(false);
        setError(laneErrorMessage(err, failMessage));
        return null;
      }
    },
    [pollSearch, stop, publish]
  );

  const start = useCallback(
    async (request: LaneSearchRequest): Promise<void> => {
      await open(
        () => CreateLaneSearch(token, request),
        'Could not start the search. Please try again.'
      );
    },
    [token, open]
  );

  const load = useCallback(
    (searchId: string): Promise<LaneSearch | null> =>
      open(
        () =>
          GetLaneSearch(token, searchId, undefined, { includeParams: true }),
        'Could not open that search. Please try again.'
      ),
    [token, open]
  );

  const retryVendors = useCallback(
    async (vendorSearchIds: string[]): Promise<void> => {
      if (!search || vendorSearchIds.length === 0) return;
      const searchId = search.id;
      stop();
      const thisGeneration = generation.current;
      setError(null);
      setSearching(true);
      // One at a time: each restart answers with the whole search.
      let restarted: LaneSearch | null = null;
      try {
        for (const vendorSearchId of vendorSearchIds) {
          const res = await RetryVendorSearch(token, searchId, vendorSearchId);
          if (thisGeneration !== generation.current) return;
          restarted = unwrapLanePayload<LaneSearch>(res);
        }
        if (!restarted) return;
        publish(restarted);
        if (restarted.done) setSearching(false);
        else pollSearch(restarted.id);
      } catch (err) {
        if (thisGeneration !== generation.current) return;
        // Rows restarted before the failure are running on the server: show and follow them.
        if (restarted) {
          publish(restarted);
          if (restarted.done) setSearching(false);
          else pollSearch(restarted.id);
        } else {
          // Nothing was restarted, but the stop() above ended the polling of the
          // vendors still running: follow the search again.
          pollSearch(searchId);
        }
        setError(
          laneErrorMessage(err, 'Could not retry the vendor. Please try again.')
        );
      }
    },
    [token, search, pollSearch, stop, publish]
  );

  const addLanes = useCallback(
    async (sites: {
      origin_sites: LaneSites;
      destination_sites: LaneSites;
    }): Promise<boolean> => {
      if (!search) return false;
      stop();
      const thisGeneration = generation.current;
      setError(null);
      setSearching(true);
      try {
        const res = await AddLaneSearchLanes(token, search.id, sites);
        if (thisGeneration !== generation.current) return true;
        const updated = unwrapLanePayload<LaneSearch>(res);
        publish(updated);
        if (updated.done) setSearching(false);
        else pollSearch(updated.id);
        return true;
      } catch (err) {
        if (thisGeneration !== generation.current) return true;
        setError(
          laneErrorMessage(err, 'Could not add those lanes. Please try again.')
        );
        // The first search may still be running, and the server may have taken
        // the lanes even though the answer was lost: read the search again.
        pollSearch(search.id);
        return false;
      }
    },
    [token, search, pollSearch, stop, publish]
  );

  const reset = useCallback((): void => {
    stop();
    publish(null);
    setSearching(false);
    setError(null);
  }, [stop, publish]);

  return {
    search,
    searching,
    error,
    start,
    load,
    retryVendors,
    addLanes,
    reset,
  };
};
